import apiService from "../../../service/Api";
import {
  getRateAmount,
  IFormProps,
  IRateCardProps,
} from "../types/rate-card";

export interface AssignmentRoleInfo {
  id: number;
  name: string;
  description?: string | null;
}

export interface AssignmentRateResolution {
  rateCard: IRateCardProps;
  amount: number;
  currency: string;
}

export interface AssignmentStreamInfo {
  id: number;
  title: string;
  date: string;
  start: string;
  end: string;
}

const getDefaultEffectiveDate = () => new Date().toISOString().split("T")[0];

const getBaseRateValue = (currency: string) => ({
  amount: 0,
  currency,
  overtime_multiplier: 0,
  night_premium: 0,
  notes: "",
});

export const createEmptyAssignmentRateForm = (
  effectiveDate?: string,
): IFormProps => ({
  role: {
    name: "",
    description: "",
  },
  roleId: null,
  hourly_rate: 0,
  stream_rate: 0,
  daily_rate: 0,
  salaried_rate: 0,
  fixed_rate: 0,
  currency: "USD",
  effective_date: effectiveDate ?? getDefaultEffectiveDate(),
  last_updated_by: "",
});

export const createAssignmentRateForm = (
  role: AssignmentRoleInfo,
  effectiveDate?: string,
): IFormProps => ({
  ...createEmptyAssignmentRateForm(effectiveDate),
  role: {
    name: role.name,
    description: role.description ?? role.name,
  },
  roleId: role.id,
});

export const getAssignmentRoleLabel = (role: AssignmentRoleInfo) =>
  `${role.id} - ${role.name}`;

export const buildFixedRatePayload = (formData: IFormProps) => {
  const amount = Number(formData.fixed_rate ?? 0);
  const baseRateValue = getBaseRateValue(formData.currency);

  return {
    role: {
      name: formData.role.name,
      description: formData.role.description,
    },
    role_id: formData.roleId,
    rate: {
      hourly: { ...baseRateValue },
      stream: { ...baseRateValue },
      daily: { ...baseRateValue },
      salaried: { ...baseRateValue },
      fixed: {
        ...baseRateValue,
        amount,
      },
    },
    currency: formData.currency,
    effective_date: formData.effective_date,
    rating_type: "fixed" as const,
  };
};

export const normalizeAssignmentRate = (
  rateCard: IRateCardProps,
): AssignmentRateResolution => ({
  rateCard,
  amount: getRateAmount(rateCard.rate, "fixed"),
  currency: rateCard.rate?.fixed?.currency ?? rateCard.currency ?? "USD",
});

export const fetchFixedRoleRate = async (
  roleId: number,
): Promise<AssignmentRateResolution | null> => {
  const response = await apiService.get<{
    results: IRateCardProps[];
  }>("/api/crews/role-ratings/", {
    role: roleId,
    rating_type: "fixed",
    page_size: 1,
  });

  const rateCard = response.data.results?.[0];

  return rateCard ? normalizeAssignmentRate(rateCard) : null;
};

interface CreatePixelPilotJobAndGigParams {
  amount: number;
  assignedTo: number | null | undefined;
  callsheetId: number;
  crewMemberId: number;
  currency: string;
  description?: string | null;
  stream: AssignmentStreamInfo;
}

export const createPixelPilotJobAndGig = async ({
  amount,
  assignedTo,
  callsheetId,
  crewMemberId,
  currency,
  description,
  stream,
}: CreatePixelPilotJobAndGigParams) => {
  const jobResponse = await apiService.post<{ id: number }>(
    "/api/jobs/pixel-pilot-jobs/",
    {
      name: stream.title,
      currency,
      stream: stream.id,
      callsheet: callsheetId,
      assigned_to: assignedTo,
      amount,
      description: description ?? stream.title,
      status: "pending",
      start_time: `${stream.date}T${stream.start}`,
      end_time: `${stream.date}T${stream.end}`,
    },
  );

  await apiService.post("/api/gigs/pixel-pilot-gigs/", {
    job_id: jobResponse.data.id,
    crew_member_id: crewMemberId,
    status: "pending",
  });

  return jobResponse.data;
};
